import { VideoProps } from 'src/components/player/player.types';
import { PostProps } from 'src/hook/usePosts';
import { ResponseScheduledPosts } from 'src/services/posts';
import { MemberProps as RankingMember } from 'src/components/memberRanking/components';

import api from '../../services/apiSparkle';
import apiGatwey from '../../services/api';
import { PropsCreatePost } from '../../components/InputMarkdown/types';
import { ROWS_POST_PER_PAGE } from '../../utils/constants';
import { PublicationEndpoints } from './constants';
import { GetPostsProps, TrendingPostsProps, StatusMedia, GetPostImageProps } from './types';

export const deletePost = async (id: number) => {
  const url = PublicationEndpoints.deletePost(id);

  const res = await api.delete(url);
  return res.data;
};

export const getPost = async (id: number, avatarSize: number) => {
  const url = PublicationEndpoints.getPost(id);

  const res = await api.get(`${url}?avatarDimensions=${avatarSize}x${avatarSize}`);
  return res.data;
};

export const readPost = async (id: number, accessToPost: string) => {
  const url = PublicationEndpoints.readPost(id, accessToPost);

  const headers = {
    'x-sparkle-appsystem': 'web'
  };

  const res = await api.put(url, undefined, {
    headers: headers
  });
  return res.data;
};

export const getPosts = async ({
  id,
  avatarSize,
  page = 0,
  filters,
  rows = ROWS_POST_PER_PAGE
}: GetPostsProps) => {
  const url = PublicationEndpoints.getPosts(id);

  const res = await api.get<ResponseScheduledPosts>(url, {
    params: {
      page,
      rows,
      avatarDimensions: `${avatarSize}x${avatarSize}`,
      pubDateOrder: filters?.pubDateOrder,
      filterReadUnread: filters?.filterReadUnread,
      myNews: filters?.myNews,
      pinned: filters?.pinned ?? null
    }
  });

  return res.data;
};

export const getTrendingPosts = async (slug: string) => {
  const response = await api.get<TrendingPostsProps[]>(PublicationEndpoints.getTrendingPosts(slug));

  return response.data;
};

export const getRankingMembers = async (communityId: number) => {
  const response = await api.get<RankingMember[]>(
    PublicationEndpoints.getRankingMembers(communityId)
  );

  return response.data;
};

export const createPost = async (body: PropsCreatePost) => {
  const url = '/rest/v1/membership/posts';
  const res = await api.post(url, body);

  const postLocation = res.headers.location.split('/');
  const postId = postLocation[postLocation.length - 1];

  return postId;
};

export const getStatusMedia = async (postsProcessing: PostProps[]) => {
  const params = new URLSearchParams();

  postsProcessing.forEach((post: PostProps) => {
    params.append('ids', post.id.toString());
  });

  const res = await api.get<StatusMedia[]>(PublicationEndpoints.getStatusMedia(), { params });

  return res.data;
};

export const getDetailsPostLesson = async (slug: string, productId: number, hash: string) => {
  const url = `${process.env.CONTENT_URL}/rest/v1/memberships/${slug}/products/${productId}/pages/${hash}/summary`;
  const res = await apiGatwey.get(url);

  return res.data;
};

export const getMedia = async (publicationId: number) => {
  const response = await api.get<VideoProps>(PublicationEndpoints.getMedia(publicationId));

  return response.data;
};

export const getAuthorImage = async (
  authorId: number,
  useMembershipUserProfile = true,
  dimension = 80,
  redirect = false
) => {
  const params = {
    useMembershipUserProfile,
    dimensions: `${dimension}x${dimension}`,
    redirect
  };

  const { data: authorImageData } = await api.get<{ data: string }>(
    `${PublicationEndpoints.getAuthorImage(authorId)}`,
    {
      params
    }
  );

  return authorImageData.data;
};

export const getPostImage = async ({
  publicationId,
  imageDimension,
  redirect = false,
  isUnauthenticated = true
}: GetPostImageProps) => {
  const params = {
    redirect,
    ...(imageDimension?.width &&
      imageDimension?.height && {
        dimensions: `${imageDimension.width}x${imageDimension.height}`
      })
  };

  const url = isUnauthenticated ? PublicationEndpoints.getPublicPostImage(publicationId) : PublicationEndpoints.getPostImage(publicationId)

  const { data: postData } = await api.get<{ data: string }>(
    `${url}`,
    {
      params
    }
  );

  return postData.data;
};

export const putScheduledPostPublishNow = async (postId: number) => {
  const res = await api.put<void>(PublicationEndpoints.putScheduledPostsPublishNow(postId));

  return res.data;
};

export const getLinkPreviewImage = async (publicationId: number, redirect = false) => {
  const params = { redirect };

  const { data: linkPreviewImageResponse } = await api.get<{ data: string }>(
    `${PublicationEndpoints.getLinkPreviewImage(publicationId)}`,
    { params }
  );

  return linkPreviewImageResponse.data;
};

export const addReaction = async (publicationId: number, reactionCode: string) => {
  const url = PublicationEndpoints.addReaction(publicationId);
  const res = await api.post(url, { reactionCode });
  return res.data;
};

export const deleteReaction = async (publicationId: number, reactionCode: string) => {
  const url = PublicationEndpoints.deleteReaction(publicationId, reactionCode);
  const res = await api.delete(url);

  return res.data;
};

export const seenReaction = async (publicationsId: string) => {
  const url = `${PublicationEndpoints.seenReaction()}?${publicationsId}`;
  const res = await api.put(url);

  return res.data;
};
